/**
 * 説明文のコンテンツを画像とテキストに分割して整形するユーティリティ
 */

export interface ContentPart {
  type: 'text' | 'image' | 'video';
  content: string;
  videoSources?: {
    webm?: string;
    mp4?: string;
  };
  poster?: string;
}

/**
 * URLからタイムスタンプパラメータを削除
 */
const cleanUrl = (url: string): string => {
  return url.replace(/\?t=\d+/, '');
};

/**
 * 説明文を画像とテキストに分割して整形
 */
export const parseDescription = (text: string): ContentPart[] => {
  if (!text) return [];

  const result: ContentPart[] = [];
  let processedText = text;

  // <picture>タグ内の重複画像を処理（アニメーション版を優先）
  processedText = processedText.replace(/<picture[^>]*>([\s\S]*?)<\/picture>/gi, (match, content) => {
    // APNGまたはGIFのアニメーション画像があるかチェック
    const animatedMatch = content.match(/https?:\/\/[^\s]+\.(?:apng|gif)\b[^\s]*/i);
    if (animatedMatch) {
      // アニメーション画像がある場合はそれだけを残す
      return animatedMatch[0];
    }
    
    // アニメーション画像がない場合は、srcsetまたは最初の画像URLを使用
    const srcsetMatch = content.match(/srcset="([^"]+)"/i);
    if (srcsetMatch) {
      return cleanUrl(srcsetMatch[1]);
    }
    
    // 他の画像URLを探す
    const imgMatch = content.match(/https?:\/\/[^\s]+\.(?:jpg|jpeg|png|webp|avif)\b[^\s]*/i);
    if (imgMatch) {
      return imgMatch[0];
    }
    
    return match; // 何も見つからない場合は元のまま
  });

  // まず<video>タグを検出して処理
  const videoRegex = /<video[^>]*>[\s\S]*?<\/video>/gi;
  const videoMatches: { match: RegExpExecArray; videoData: ContentPart }[] = [];
  
  let videoMatch;
  while ((videoMatch = videoRegex.exec(processedText)) !== null) {
    const videoHtml = videoMatch[0];
    
    // poster属性を抽出
    const posterMatch = videoHtml.match(/poster="([^"]+)"/i);
    const poster = posterMatch ? cleanUrl(posterMatch[1]) : undefined;
    
    // source要素からWebMとMP4のURLを抽出
    const webmMatch = videoHtml.match(/<source[^>]+src="([^"]+)"[^>]+type="video\/webm[^"]*"/i);
    const mp4Match = videoHtml.match(/<source[^>]+src="([^"]+)"[^>]+type="video\/mp4[^"]*"/i);
    
    const videoSources = {
      webm: webmMatch ? cleanUrl(webmMatch[1]) : undefined,
      mp4: mp4Match ? cleanUrl(mp4Match[1]) : undefined,
    };

    // 動画データを作成
    const videoData: ContentPart = {
      type: 'video',
      content: poster || '', // posterをcontentとして使用
      videoSources,
      poster
    };

    videoMatches.push({ match: videoMatch, videoData });
  }

  // videoタグを削除した後のテキストを作成
  videoMatches.forEach(({ match }) => {
    processedText = processedText.replace(match[0], `__VIDEO_PLACEHOLDER_${videoMatches.indexOf(videoMatches.find(v => v.match === match)!)}__`);
  });

  // 残りのテキストから画像URLを検出する正規表現（ただしposterのAVIFは除外）
  const posterUrls = videoMatches.map(v => v.videoData.poster).filter(Boolean);
  const urlRegex = /(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|avif|apng))\s*/gi;
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(processedText)) !== null) {
    const imageUrl = cleanUrl(match[1]);
    
    // posterのAVIFファイルまたは.poster.付きの静止画ファイルはスキップ
    if (posterUrls.some(posterUrl => posterUrl === imageUrl) || 
        /\.poster\.(avif|webp|jpg|jpeg|png)(\?.*)?$/i.test(imageUrl)) {
      continue;
    }

    // URLの前のテキストを追加（存在する場合）
    if (match.index > lastIndex) {
      const textContent = processedText.slice(lastIndex, match.index).trim();
      if (textContent) {
        // VIDEO_PLACEHOLDERがあるかチェックして動画に置き換え
        const processedContent = replaceVideoPlaceholders(textContent, videoMatches);
        if (processedContent.length > 0) {
          result.push(...processedContent);
        }
      }
    }

    // 画像URLを追加
    result.push({
      type: 'image',
      content: imageUrl
    });

    lastIndex = urlRegex.lastIndex;
  }

  // 残りのテキストを追加（存在する場合）
  if (lastIndex < processedText.length) {
    const remainingText = processedText.slice(lastIndex).trim();
    if (remainingText) {
      const processedContent = replaceVideoPlaceholders(remainingText, videoMatches);
      if (processedContent.length > 0) {
        result.push(...processedContent);
      }
    }
  }

  return result;
};

/**
 * テキスト内のVIDEO_PLACEHOLDERを動画データに置き換える
 */
const replaceVideoPlaceholders = (text: string, videoMatches: { match: RegExpExecArray; videoData: ContentPart }[]): ContentPart[] => {
  const result: ContentPart[] = [];
  let currentText = text;

  videoMatches.forEach((videoMatch, index) => {
    const placeholder = `__VIDEO_PLACEHOLDER_${index}__`;
    const parts = currentText.split(placeholder);
    
    if (parts.length > 1) {
      // プレースホルダーの前のテキスト
      if (parts[0].trim()) {
        result.push({
          type: 'text',
          content: formatText(parts[0].trim())
        });
      }
      
      // 動画データ
      result.push(videoMatch.videoData);
      
      // プレースホルダーの後のテキストを更新
      currentText = parts.slice(1).join(placeholder);
    }
  });

  // 残りのテキスト
  if (currentText.trim()) {
    result.push({
      type: 'text',
      content: formatText(currentText.trim())
    });
  }

  return result;
};

/**
 * テキストの整形
 */
const formatText = (text: string): string => {
  let formattedText = text
    // 見出しタグはそのまま保持
    // 3つ以上連続したbrタグを2つに制限
    .replace(/(<br\s*\/?>\s*){2,}/gi, '<br><br>')
    .replace(/(<br\s*\/?>\s*)/gi, '<br>')
    // 行頭のスペースを削除
    .replace(/^\s+/gm, '')
    // 行末のスペースを削除
    .replace(/\s+$/gm, '')
    // 全角スペースを半角に変換
    .replace(/　/g, ' ')
    // 複数の半角スペースを1つに
    .replace(/ +/g, ' ')
    // HTMLエンティティをデコード
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  return formattedText.trim();
};

/**
 * 説明文にアニメーション画像が含まれているかチェック（GIF、AVIF、一部のPNG）
 */
export const containsAnimatedImage = (text: string | null): boolean => {
  if (!text) return false;
  const animatedRegex = /https?:\/\/[^\s]+\.(?:gif|avif|apng|png)\b/i;
  return animatedRegex.test(text);
};

/**
 * 説明文に動画が含まれているかチェック
 */
export const containsVideo = (text: string | null): boolean => {
  if (!text) return false;
  const videoRegex = /<video[^>]*>[\s\S]*?<\/video>/i;
  return videoRegex.test(text);
};

/**
 * 画像URLがアニメーション形式かどうかをチェック
 */
export const isAnimatedImage = (url: string): boolean => {
  return /\.(?:gif|avif|apng)$/i.test(url);
}; 